import { COLORS } from '@app/constants';
import { useUnifiedWallet } from '@app/hooks/wallet/useUnifiedWallet';
import { useDemoTradingStore } from '@app/hooks/trade/demoTradingStore';
import BigNumber from 'bignumber.js';
import React, { useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Text, View } from '@/styles/reactnative';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useIndexPrice } from '../../../../../../hooks/trade/indexprice';
import { useTradePosition } from '../../../../../../hooks/trade/tradePosition';
import useTradeStore from '../../../../../../hooks/trade/tradeStore';
import { useBalance } from '../../../../../../hooks/user/mybalance';
import { useTradeSetting } from '../../../../../../hooks/trade/tradeSetting';
import DerivativesAccountBtns from './DerivativesAccountBtns';
import DerivativesAccountList from './DerivativesAccountList';
import { useIsMobile } from '@utils/CalVW.tsx';
import styled from 'styled-components';

const DerivativesAccount = ({ isMobile = false }) => {
  const { t } = useTranslation();
  const { textw } = useContext(ThemeContext).theme.COLORS;
  const { data: tradeStore } = useTradeStore();
  const currency = tradeStore?.pairs?.split('/')[1];
  const { data: tradeSetting } = useTradeSetting(tradeStore?.ep_id);

  // ==========================================
  // 모의거래 모드 확인
  // ==========================================
  const { isDemoMode, account } = useDemoTradingStore(); // ✅ account 추가

  // ==========================================
  // 신규 API: 통합 지갑 조회
  // ==========================================
  const { data: unifiedWallets } = useUnifiedWallet();

  // ==========================================
  // Legacy API: 실거래 지갑 조회 (Fallback)
  // ==========================================
  const { data: legacyBalance } = useBalance(currency, 1);

  const { data: position } = useTradePosition();

  const { getPrice } = useIndexPrice(tradeStore?.ep_id);

  const open_pos = position;

  // ==========================================
  // PnL 계산 (Cross 모드만)
  // ==========================================
  let pnl = 0;
  for (let i = 0; i < open_pos?.length; i++) {
    if (open_pos[i].cross_iso == 'I') continue;

    if (open_pos[i].long_short === 'L') {
      pnl +=
        (getPrice(open_pos?.[i]?.exchange_pairs_id) -
          open_pos?.[i]?.open_price) *
        open_pos[i]?.amount *
        open_pos[i]?.leverage;
    } else {
      pnl +=
        (open_pos[i]?.open_price - getPrice(open_pos?.[i]?.exchange_pairs_id)) *
        open_pos[i]?.amount *
        open_pos[i]?.leverage;
    }
  }

  // ==========================================
  // 조건부 데이터 소스 선택
  // ==========================================
  // 모의거래 모드: Unified Wallet 우선 (서버 데이터), Zustand는 fallback
  // 실거래 모드: Real Futures 지갑 사용
  const currentBalance = isDemoMode
    ? unifiedWallets?.demo?.balance || account.balance || '0' // ✅ Unified Wallet 우선
    : unifiedWallets?.real?.futures?.balance || legacyBalance?.balance || '0';

  const currentLockBalance = isDemoMode
    ? unifiedWallets?.demo?.lockbalance || account.lockBalance || '0' // ✅ Unified Wallet 우선
    : unifiedWallets?.real?.futures?.lockbalance ||
      legacyBalance?.lockbalance ||
      '0';

  // ==========================================
  // 가용잔액 계산 (balance - lockbalance + PnL)
  // ==========================================
  let availableBalance = new BigNumber(currentBalance)
    .minus(currentLockBalance)
    .plus(pnl)
    .toNumber();

  availableBalance = Math.max(availableBalance, 0);

  // ==========================================
  // 마진율 계산 (실시간 반영)
  // ==========================================
  const equity = BigNumber(currentBalance).plus(pnl).toNumber();
  const currentCrossIso = tradeSetting?.cross_iso;
  const isCross = currentCrossIso === 'C';

  let totalMargin = 0;
  let hasCrossPosition = false;

  if (isCross) {
    // 교차 모드: 모든 교차 포지션의 마진 합계
    open_pos?.forEach((pos: any) => {
      if (pos.cross_iso === 'C') {
        totalMargin += Number(pos.margin || 0);
        hasCrossPosition = true;
      }
    });
  } else {
    // 격리 모드: 현재 종목의 포지션 마진
    const currentPos = open_pos?.find(
      (pos: any) =>
        +pos.exchange_pairs_id === +tradeStore?.ep_id && pos.cross_iso === 'I'
    );
    if (currentPos) {
      totalMargin = Number(currentPos.margin || 0);
    }
  }

  const marginRate = equity > 0 ? (totalMargin / equity) * 100 : 0;
  const marginRateInfo = { rate: marginRate, isCross, hasCrossPosition };

  const getMarginRateColor = (rate: number) => {
    if (rate <= 5) return COLORS.lightGreen;
    if (rate <= 30) return '#FFA500'; // 주황색
    return COLORS.lightRed;
  };

  return (
    <View
      style={{
        alignItems: 'flex-start',
        marginTop: 24,
        borderTop: `1px solid rgba(53, 53, 53, 0.60)`,
        padding: '24px 12px',
        width: '100%',
      }}
    >
      <TextStyle
        style={{
          color: '#fff',
          marginBottom: 10,
          fontFamily: 'NanumSquareRound',
          fontWeight: 700,
          letterSpacing: -0.39,
          fontSize: 14,
        }}
      >
        {t('derivativesAccount')}
      </TextStyle>

      {/* 마진율 */}
      <View
        style={{
          flexDirection: 'row',
          justifyContent: 'space-between',
          width: '100%',
        }}
      >
        <TextStyle style={{ color: '#B5B5BC', fontSize: 12 }}>
          {marginRateInfo.isCross && marginRateInfo.hasCrossPosition
            ? t('usdtCross')
            : t('marginRate')}
        </TextStyle>
        <TextStyle
          style={{
            color: getMarginRateColor(marginRateInfo.rate),
            fontWeight: 400,
            fontSize: 12,
          }}
        >
          {BigNumber(marginRateInfo.rate).toFixed(2)}%
        </TextStyle>
      </View>

      <DerivativesAccountList
        title={'equity'}
        amount={
          isNaN(BigNumber(currentBalance).plus(pnl).toNumber())
            ? 0
            : BigNumber(currentBalance).plus(pnl).toNumber()
        }
      />

      <DerivativesAccountList
        title={'availableBalance'}
        amount={availableBalance}
      />

      <DerivativesAccountBtns />
    </View>
  );
};

export default DerivativesAccount;

const TextStyle = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: row;
  font-family: 'NanumSquareRound', sans-serif !important;
  font-size: 14px;
  line-height: 17px;
  color: #fff;
`;
